import { useCallback, useMemo } from 'react';
import _ from 'lodash';
import moment from 'moment';

import { timeRangeFilterId } from '../components/filters';
import {
    CUSTOM_ID,
    MIN_DATE_TIME_VALUE,
    MIN_DATE_TIME_RANGE,
    MAX_DATE_TIME_RANGE,
    TIME_RANGE_PERIODS,
    getPeriod,
} from '../components/filters/time-range-filter/TimeRangeFilterConstants';
import { getPeriodTimeRange } from '../components/filters/time-range-filter/TimeRangeFilterStateService';
import { useCurrentFilterStateById } from '../stores/FilterStateStore';
import { useTimeRangeHistory } from '../stores/TimeRangeHistoryStore';
import { getSystemTime } from '../stores/SystemTimeStore';

export const SHIFT_MULTIPLIER = 0.5; // shifts half the overall timespan
export const ZOOM_IN_MULTIPLIER = 0.5; // zooms into half of the timespan (halves the timespan)
export const ZOOM_OUT_MULTIPLIER = -1; // zooms one full timespan (double the timespan)

const TIMERANGE_ZOOM_OUT = 1;
const TIMERANGE_ZOOM_IN = -1;

const TIME_RANGE_IDS = _.map(TIME_RANGE_PERIODS, 'id');

/**
 * @private
 *
 * Zooms the current timerange in a given direction (TIMERANGE_ZOOM_OUT/TIMERANGE_ZOOM_IN). If the timerange is relative
 * and the next zoom direction is also relative, the next relative timerange will be set. Otherwise, it will become a
 * zoomed custom range
 *
 * @param {Number} direction The zoom direction
 */
function zoomTimerange(timerange, direction) {
    const nextRelative = getDirectionalRelativeTimerange(timerange, direction);
    const multiplier = direction === TIMERANGE_ZOOM_OUT ? ZOOM_OUT_MULTIPLIER : ZOOM_IN_MULTIPLIER;

    if (nextRelative)
        return {
            id: nextRelative,
            ...getPeriodTimeRange(getPeriod(nextRelative)),
        };

    return shiftTimerange(timerange, multiplier, 0);
}

/**
 * @private
 *
 * Returns the next relative timerange ID. This is used for zooming in/out of relative
 * timeranges
 *
 * @param {Number} direction The direction. Should be `TIMERANGE_ZOOM_IN or TIMERANGE_ZOOM_OUT`.
 * @return {String} The ID of the next relative timerange, or null if one could not be found
 */
function getDirectionalRelativeTimerange(timerange, direction) {
    const { id } = timerange;
    const index = _.indexOf(TIME_RANGE_IDS, id);

    if (index > -1) {
        const offset = direction === TIMERANGE_ZOOM_OUT ? 1 : -1;
        return TIME_RANGE_IDS[index + offset];
    }
}

/**
 * Shifts the provided timerange. This applies multipliers to the start and/or end time value. If a multiplier
 * is zero, the date for that time range will not change. A value of `0.5` would half the timerange and a value
 * of `1` would double the timerange.
 *
 * @param {Object} timerange The timerange filter value
 * @param {Number} startMultipler The multiplier to apply to the start timerange
 * @param {Number} endMultiplier The multiplier to apply to the end timerange
 * @return {Object} A key/value object to use in setFilterState
 */
export function shiftTimerange(timerange, startMultipler, endMultiplier) {
    let startMs = getMsOf(timerange.start);
    let endMs = getMsOf(timerange.end);
    const diff = endMs - startMs;
    const startOffset = normalizeOffset(diff * startMultipler);
    const endOffset = normalizeOffset(diff * endMultiplier);

    startMs += startOffset;
    endMs += endOffset;

    // first, make sure end > start. If not, swap them
    if (endMs < startMs) {
        const tmp = endMs;
        endMs = startMs;
        startMs = tmp;
    }

    // drop anything more accurate than the minimum time range (aka, one minute)
    startMs -= startMs % MIN_DATE_TIME_RANGE;
    endMs -= endMs % MIN_DATE_TIME_RANGE;

    return {
        start: getDateOf(startMs),
        end: getDateOf(endMs),
    };
}

/**
 * Returns a new time range which has been constrained to the min/max
 * values defined in the timerange filter constants
 *
 * @param {Object} timerange
 * @param {Object} options
 * @return {Object}
 */
export function getConstrainedTimerange(timerange, options) {
    const { constrainRange = false, constrainStart = false, constrainEnd = false } = options || {};

    let startMs = getMsOf(timerange.start);
    let endMs = getMsOf(timerange.end);

    // if we want to make sure we do not go into the future
    if (constrainEnd === true) {
        const now = getSystemTime().valueOf();

        if (endMs > now) {
            const offset = endMs - now;
            endMs -= offset;
            startMs -= offset;
        }
    }

    if (constrainStart === true && startMs < MIN_DATE_TIME_VALUE) {
        const offset = MIN_DATE_TIME_VALUE - startMs;

        startMs += offset;
        endMs += offset;
    }

    // if we want to make suer we do not exceed our max/min ranges
    if (constrainRange === true) {
        const diff = endMs - startMs;

        if (diff > MAX_DATE_TIME_RANGE) {
            startMs = endMs - MAX_DATE_TIME_RANGE;
        } else if (diff < MIN_DATE_TIME_RANGE) {
            startMs = endMs - MIN_DATE_TIME_RANGE;
        }
    }

    // drop anything more accurate than the minimum time range (aka, one minute)
    startMs -= startMs % MIN_DATE_TIME_RANGE;
    endMs -= endMs % MIN_DATE_TIME_RANGE;

    return {
        start: getDateOf(startMs),
        end: getDateOf(endMs),
    };
}

/**
 * Returns the milliseconds in a given date, moment date. If a number
 * is passed it, it is returned
 *
 * @param {Date/Moment/Number} dateOrMoment
 * @return {Number}
 */
export function getMsOf(dateOrMoment) {
    if (moment.isDate(dateOrMoment)) return dateOrMoment.getTime();
    if (moment.isMoment(dateOrMoment)) return dateOrMoment.toDate().getTime();
    if (_.isFinite(dateOrMoment)) return dateOrMoment;

    return null;
}

/**
 * @private
 * Returns the date/moment/milliseconds into a javascript date object
 *
 * @param {Moment/Date/Number} dateOrMoment
 * @return {Date}
 */
function getDateOf(dateOrMoment) {
    if (moment.isDate(dateOrMoment)) return dateOrMoment;
    if (moment.isMoment(dateOrMoment)) return dateOrMoment.toDate();
    if (_.isFinite(dateOrMoment)) return moment(dateOrMoment).toDate();

    return null;
}

/**
 * @private
 * Normalizes an offset to be at least one minute, either in the positive or negative direction. If offset is 0, then
 * 0 is returned.
 *
 * @param {Number} offset
 * @return {Number}
 */
function normalizeOffset(offset) {
    if (offset < 0) return Math.min(-MIN_DATE_TIME_RANGE, offset);
    if (offset > 0) return Math.max(MIN_DATE_TIME_RANGE, offset);

    return offset;
}

export function useTimeRangeNavigation() {
    const [timeRangeFilterState, filterStateActions] = useCurrentFilterStateById(timeRangeFilterId);
    const [timeRangeHistory, timeRangeHistoryActions] = useTimeRangeHistory();
    const setTimeRange = useCallback(
        (timerange) => {
            const range = _.assignIn({ id: CUSTOM_ID, start: new Date(0), end: new Date(0) }, timerange);

            if (range.id === CUSTOM_ID) {
                const constrained = getConstrainedTimerange(range, {
                    constrainRange: true,
                    constrainStart: true,
                    constrainEnd: true,
                });
                range.start = constrained.start;
                range.end = constrained.end;
            }

            filterStateActions.setFilterState(timeRangeFilterId, range);
        },
        [filterStateActions]
    );
    const actions = useMemo(() => {
        return {
            enabled: !!timeRangeFilterState,
            panBack() {
                if (timeRangeHistory.canPanBack)
                    setTimeRange(shiftTimerange(timeRangeFilterState, -SHIFT_MULTIPLIER, -SHIFT_MULTIPLIER));
            },
            panForward() {
                if (timeRangeHistory.canPanForward)
                    setTimeRange(shiftTimerange(timeRangeFilterState, SHIFT_MULTIPLIER, SHIFT_MULTIPLIER));
            },
            zoomIn() {
                if (timeRangeHistory.canZoomIn) setTimeRange(zoomTimerange(timeRangeFilterState, TIMERANGE_ZOOM_IN));
            },
            zoomOut() {
                if (timeRangeHistory.canZoomOut) setTimeRange(zoomTimerange(timeRangeFilterState, TIMERANGE_ZOOM_OUT));
            },
            reset() {
                if (timeRangeHistory.canReset) {
                    filterStateActions.setFilterState(timeRangeFilterId, timeRangeHistory.history[0]);
                    timeRangeHistoryActions.resetHistory();
                }
            },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeRangeHistory, timeRangeFilterState, setTimeRange]);

    return actions;
}
